import * as React from "react";
import { Link } from "react-router-dom";
import { useLiveDashboard, AgentData, TeamData, TopAgent, ChannelBreakdown, DashboardData } from "@/hooks/useLiveDashboard";
import useApi from "@/hooks/useApi";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";

// ── Status dot helpers ────────────────────────────────────────────────────────

function statusColor(status: string, currentChats: number, maxChats: number): string {
  if (status === "Offline") return "bg-slate-400";
  if (status === "Available") return "bg-success-green-500";
  if (status === "Busy" || (maxChats > 0 && currentChats >= maxChats)) return "bg-red-500";
  return "bg-sweet-orange-500";
}

function StatusDot({ status, currentChats, maxChats }: { status: string; currentChats: number; maxChats: number }) {
  return (
    <span
      aria-label={`Status: ${status}`}
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${statusColor(status, currentChats, maxChats)}`}
    />
  );
}

// ── Connection bar ────────────────────────────────────────────────────────────

function ConnectionBar({
  status, lastUpdated, isManagerView
}: {
  status: "connecting" | "live" | "reconnecting" | "error";
  lastUpdated: Date | null;
  isManagerView: boolean;
}) {
  const dotClass = status === "live" ? "bg-green-500" : "bg-amber-500";
  const label = status === "live" ? "Live" : status === "reconnecting" ? "Reconnecting..." : "Connecting...";

  return (
    <div className="flex items-center justify-between px-4 py-2 rounded-lg mb-4 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className={`inline-block w-2.5 h-2.5 rounded-full ${dotClass} ${status === "live" ? "animate-pulse" : ""}`} />
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        {status === "live" && lastUpdated && (
          <span className="text-slate-400 dark:text-slate-500">&middot; Last updated {lastUpdated.toLocaleTimeString()}</span>
        )}
        {status === "reconnecting" && lastUpdated && (
          <span className="text-slate-400 dark:text-slate-500 text-xs">&middot; Showing data from {lastUpdated.toLocaleTimeString()}</span>
        )}
      </div>
      {isManagerView && (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
          My Teams
        </span>
      )}
    </div>
  );
}

// ── KPI strip ─────────────────────────────────────────────────────────────────

function InfoIcon({ tip }: { tip: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label="More information"
        className="text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-full"
        onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <span role="tooltip" className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-slate-800 dark:bg-slate-950 text-white text-xs px-3 py-2 shadow-lg pointer-events-none">
          {tip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800 dark:border-t-slate-950" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}

function KpiCard({ label, value, sub, warn, color, tip }: {
  label: string; value: string | number; sub?: string;
  warn?: boolean; color?: "green" | "red" | "amber" | "blue"; tip?: string
}) {
  const valueClass =
    color === "green" ? "text-green-600 dark:text-green-400" :
    color === "red" ? "text-red-600 dark:text-red-400" :
    color === "amber" ? "text-amber-600 dark:text-amber-400" :
    color === "blue" ? "text-blue-600 dark:text-blue-400" :
    warn ? "text-amber-600 dark:text-amber-400" : "text-slate-800 dark:text-slate-100";

  return (
    <div className={`rounded-lg p-4 flex flex-col bg-white dark:bg-slate-800 border ${warn ? "border-amber-300 dark:border-amber-600" : "border-slate-200 dark:border-slate-700"}`}>
      <span className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}{tip && <InfoIcon tip={tip} />}
      </span>
      <span className={`font-bold mt-1 ${valueClass} ${String(value).length > 5 ? "text-xl" : "text-3xl"}`} title={String(value)}>
        {value}
      </span>
      {sub && <span className="text-xs mt-0.5 text-slate-400 dark:text-slate-500">{sub}</span>}
    </div>
  );
}

function formatMinutes(mins: number): string {
  if (mins < 1) return "< 1m";
  const m = Math.floor(mins);
  const s = Math.round((mins - m) * 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ── Agent row ─────────────────────────────────────────────────────────────────

function AgentRow({ agent }: { agent: AgentData }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <StatusDot status={agent.status} currentChats={agent.currentChats} maxChats={agent.maxChats} />
      <span className="flex-1 min-w-0 truncate text-sm text-slate-700 dark:text-slate-300">{agent.displayName}</span>
      <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{agent.currentChats}/{agent.maxChats || "∞"}</span>
    </div>
  );
}

// ── Top Agents widget ─────────────────────────────────────────────────────────

function TopAgentsWidget({ agents }: { agents: TopAgent[] }) {
  if (agents.length === 0) return null;
  return (
    <div className="rounded-lg p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">Top Agents Today</h3>
      <div className="space-y-2">
        {agents.map((a, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-400 dark:text-slate-500 w-4 text-right">{i + 1}</span>
            <span className="flex-1 min-w-0 truncate text-sm text-slate-700 dark:text-slate-300">{a.name}</span>
            <span className="text-xs text-green-600 dark:text-green-400 font-medium tabular-nums">{a.resolved} resolved</span>
            {a.active > 0 && <span className="text-xs text-blue-500 dark:text-blue-400 tabular-nums">{a.active} active</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Channel Breakdown widget ──────────────────────────────────────────────────

const CHANNEL_COLORS: Record<string, string> = {
  Web: "bg-blue-500", SMS: "bg-green-500", WhatsApp: "bg-emerald-500",
  Facebook: "bg-indigo-500", "Twitter (X)": "bg-sky-500", IVR: "bg-violet-500",
  Email: "bg-amber-500", Ticket: "bg-orange-500",
};

function ChannelBreakdownWidget({ channels }: { channels: ChannelBreakdown[] }) {
  if (channels.length === 0) return null;
  const total = channels.reduce((s, c) => s + c.count, 0);
  return (
    <div className="rounded-lg p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">Active by Channel</h3>
      <div className="space-y-2">
        {channels.map((c) => {
          const pct = total > 0 ? Math.round((c.count / total) * 100) : 0;
          return (
            <div key={c.channel}>
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="text-slate-700 dark:text-slate-300">{c.channel}</span>
                <div className="flex items-center gap-2">
                  {c.queued > 0 && <span className="text-amber-600 dark:text-amber-400">{c.queued} queued</span>}
                  <span className="text-slate-500 dark:text-slate-400 tabular-nums">{c.count} ({pct}%)</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${CHANNEL_COLORS[c.channel] ?? "bg-slate-400"}`} style={{ width: `${pct}%` }} aria-hidden="true" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Coverage badge ────────────────────────────────────────────────────────────

function CoverageBadge({ status, unassigned }: { status: string; unassigned: number }) {
  if (status === "covered") return null;
  if (status === "no_agents") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700" title="No agents are logged in to this team">
        <span aria-hidden="true">⚠</span> No agents online
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700" title="Agents are online but all are busy">
      <span aria-hidden="true">⚠</span> All agents busy{unassigned > 0 && ` · ${unassigned} waiting`}
    </span>
  );
}

// ── Team card ─────────────────────────────────────────────────────────────────

function TeamCard({ team, warnYellowMins, warnRedMins, showWarnings, expandOverride }: {
  team: TeamData; warnYellowMins: number; warnRedMins: number;
  showWarnings: boolean; expandOverride: boolean | null;
}) {
  const [expanded, setExpanded] = React.useState(true);
  const prevOverride = React.useRef(expandOverride);
  React.useEffect(() => {
    if (expandOverride !== null && expandOverride !== prevOverride.current) {
      prevOverride.current = expandOverride;
      setExpanded(expandOverride);
    }
  }, [expandOverride]);

  const hasCoverageGap = team.coverageStatus !== "covered";
  const borderClass = hasCoverageGap
    ? team.coverageStatus === "no_agents" ? "border-red-400 dark:border-red-600" : "border-amber-400 dark:border-amber-600"
    : !showWarnings ? "border-slate-200 dark:border-slate-700"
    : team.longestWaitMinutes > warnRedMins ? "border-red-300 dark:border-red-700"
    : team.longestWaitMinutes > warnYellowMins ? "border-amber-300 dark:border-amber-600"
    : "border-slate-200 dark:border-slate-700";

  return (
    <div className={`rounded-lg p-4 flex flex-col gap-2 bg-white dark:bg-slate-800 border-2 ${borderClass}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base text-slate-800 dark:text-slate-100 truncate">{team.teamName}</h3>
          <div className="flex flex-wrap gap-2 mt-1 items-center">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {team.openConversations} open
              {team.unassignedConversations > 0 && (
                <span className="ml-1 text-amber-600 dark:text-amber-400 font-medium">({team.unassignedConversations} unassigned)</span>
              )}
            </span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">Longest: {formatMinutes(team.longestWaitMinutes)}</span>
            <CoverageBadge status={team.coverageStatus ?? "covered"} unassigned={team.unassignedConversations ?? 0} />
          </div>
        </div>
        <div className="text-right ml-2">
          <div className="text-xl font-bold text-green-600 dark:text-green-400">{team.agentsOnline}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">online</div>
        </div>
      </div>
      <div className="flex gap-4 text-xs text-slate-500 dark:text-slate-400">
        <span><span className="font-medium text-green-600 dark:text-green-400">{team.agentsAvailable}</span> available</span>
        <span><span className="font-medium text-red-500 dark:text-red-400">{team.agentsBusy}</span> busy</span>
        <span>avg {formatMinutes(team.avgWaitMinutes)}</span>
      </div>
      {team.agents.length > 0 && (
        <div>
          <button className="text-xs font-medium mb-1 text-blue-500 dark:text-blue-400 hover:underline" onClick={() => setExpanded(v => !v)} aria-expanded={expanded}>
            {expanded ? "Hide agents" : `Show ${team.agents.length} agents`}
          </button>
          {expanded && (
            <div className="max-h-52 overflow-y-auto pr-1">
              {team.agents.map(agent => <AgentRow key={agent.userId} agent={agent} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Alert system ──────────────────────────────────────────────────────────────

interface DashboardAlert {
  id: string;
  severity: "error" | "warning" | "info";
  title: string;
  detail: string;
}

interface AlertThresholds {
  queueDepth: number;
  missedToday: number;
  allOffline: boolean;
}

function computeAlerts(data: DashboardData, thresholds: AlertThresholds, redMins: number): DashboardAlert[] {
  const alerts: DashboardAlert[] = [];

  // All agents offline while conversations exist
  if (thresholds.allOffline && data.kpis.agentsOnline === 0 && data.kpis.openConversations > 0) {
    alerts.push({
      id: "all_offline", severity: "error",
      title: "No agents online",
      detail: `${data.kpis.openConversations} open conversation${data.kpis.openConversations !== 1 ? "s" : ""} with no one logged in to respond.`
    });
  }

  // Coverage gaps
  const gaps = data.teams.filter(t => t.coverageStatus !== "covered");
  if (gaps.length > 0) {
    const noAgents = gaps.filter(t => t.coverageStatus === "no_agents");
    const allBusy  = gaps.filter(t => t.coverageStatus === "no_coverage");
    const parts = [
      noAgents.length > 0 ? `${noAgents.length} team${noAgents.length > 1 ? "s" : ""} with no agents online` : "",
      allBusy.length  > 0 ? `${allBusy.length} team${allBusy.length > 1 ? "s" : ""} with all agents busy`   : ""
    ].filter(Boolean);
    alerts.push({
      id: "coverage_gap",
      severity: noAgents.length > 0 ? "error" : "warning",
      title: `Coverage gap${gaps.length > 1 ? ` across ${gaps.length} teams` : ""}`,
      detail: parts.join(" · ") + ". Check routing rules or unavailability workflows."
    });
  }

  // Queue depth
  if (data.kpis.queuedConversations > thresholds.queueDepth) {
    alerts.push({
      id: "queue_depth", severity: "warning",
      title: `Queue depth: ${data.kpis.queuedConversations} waiting`,
      detail: `Exceeds your threshold of ${thresholds.queueDepth}. Add agents or adjust routing.`
    });
  }

  // Longest wait exceeded red threshold
  if (data.kpis.longestWaitMinutes > redMins) {
    alerts.push({
      id: "long_wait", severity: "warning",
      title: `Longest wait: ${formatMinutes(data.kpis.longestWaitMinutes)}`,
      detail: `One or more conversations have been queued over ${redMins} minutes without an agent.`
    });
  }

  // Missed/abandoned today
  if (data.kpis.missedAbandonedToday > thresholds.missedToday) {
    alerts.push({
      id: "missed_today", severity: "warning",
      title: `${data.kpis.missedAbandonedToday} missed or abandoned today`,
      detail: `Exceeds your threshold of ${thresholds.missedToday}. Review staffing levels and routing rules.`
    });
  }

  // Bot covering but no humans available — chats won't escalate
  if (gaps.length > 0 && data.kpis.botActive > 0 && data.kpis.agentsAvailable === 0) {
    alerts.push({
      id: "bot_no_escalation", severity: "info",
      title: "Bot is handling chats — escalation unavailable",
      detail: `${data.kpis.botActive} conversation${data.kpis.botActive !== 1 ? "s" : ""} are bot-managed but no agents are available if a handoff is needed.`
    });
  }

  return alerts;
}

// ── Dismiss menu ──────────────────────────────────────────────────────────────

function DismissMenu({ onDismiss }: { onDismiss: (type: "1h" | "4h" | "permanent") => void }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
      >
        Dismiss
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 overflow-hidden">
          {(["1h", "4h"] as const).map(t => (
            <button key={t} onClick={() => { onDismiss(t); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
              Snooze for {t === "1h" ? "1 hour" : "4 hours"}
            </button>
          ))}
          <div className="border-t border-slate-100 dark:border-slate-700">
            <button onClick={() => { onDismiss("permanent"); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
              Don't show this again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Alert strip ───────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  error:   { border: "border-red-400 dark:border-red-600",   bg: "bg-red-50 dark:bg-red-900/20",   dot: "bg-red-500",   text: "text-red-700 dark:text-red-300",   sub: "text-red-500 dark:text-red-400" },
  warning: { border: "border-amber-400 dark:border-amber-600", bg: "bg-amber-50 dark:bg-amber-900/20", dot: "bg-amber-400", text: "text-amber-700 dark:text-amber-300", sub: "text-amber-500 dark:text-amber-400" },
  info:    { border: "border-blue-300 dark:border-blue-600",  bg: "bg-blue-50 dark:bg-blue-900/20",  dot: "bg-blue-400",  text: "text-blue-700 dark:text-blue-300",  sub: "text-blue-500 dark:text-blue-400" },
};

interface Dismissal { until?: number; permanent?: boolean; }
type DismissedAlerts = Record<string, Dismissal>;

function isAlertSuppressed(id: string, dismissed: DismissedAlerts): boolean {
  const d = dismissed[id];
  if (!d) return false;
  if (d.permanent) return true;
  if (d.until && Date.now() < d.until) return true;
  return false;
}

function AlertStrip({ alerts, dismissed, onDismiss }: {
  alerts: DashboardAlert[];
  dismissed: DismissedAlerts;
  onDismiss: (id: string, type: "1h" | "4h" | "permanent") => void;
}) {
  const visible = alerts.filter(a => !isAlertSuppressed(a.id, dismissed));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {visible.map(alert => {
        const s = SEVERITY_STYLES[alert.severity];
        return (
          <div key={alert.id} className={`rounded-lg px-4 py-3 border ${s.border} ${s.bg} flex flex-wrap items-start gap-3`}>
            <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.dot}`} aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <span className={`font-semibold text-sm ${s.text}`} aria-live="polite">{alert.title}</span>
              <span className={`text-xs ml-2 ${s.sub}`}>{alert.detail}</span>
            </div>
            <DismissMenu onDismiss={type => onDismiss(alert.id, type)} />
          </div>
        );
      })}
    </div>
  );
}

// ── Settings panel ────────────────────────────────────────────────────────────

function ThresholdSettings({
  showWarnings, setShowWarnings, yellowMins, setYellowMins, redMins, setRedMins,
  alertThresholds, setAlertThresholds, dismissed, onResetAll, onResetOne, onClose
}: {
  showWarnings: boolean; setShowWarnings: (v: boolean) => void;
  yellowMins: number; setYellowMins: (v: number) => void;
  redMins: number; setRedMins: (v: number) => void;
  alertThresholds: AlertThresholds; setAlertThresholds: (v: AlertThresholds) => void;
  dismissed: DismissedAlerts; onResetAll: () => void;
  onResetOne: (id: string) => void; onClose: () => void;
}) {
  const dismissedIds = Object.keys(dismissed).filter(id => isAlertSuppressed(id, dismissed));
  const ALERT_LABELS: Record<string, string> = {
    all_offline: "No agents online",
    coverage_gap: "Coverage gaps",
    queue_depth: "Queue depth",
    long_wait: "Longest wait",
    missed_today: "Missed/abandoned today",
    bot_no_escalation: "Bot covering, no escalation",
  };

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-4 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-200">Dashboard Alert Settings</h4>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs">Close</button>
      </div>

      {/* Queue time card coloring */}
      <div>
        <h5 className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Team card queue time coloring</h5>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer mb-2">
          <input type="checkbox" checked={showWarnings} onChange={e => setShowWarnings(e.target.checked)} className="rounded" />
          Color team cards by queue wait time
        </label>
        {showWarnings && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-5">
            <div>
              <label className="block text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">Yellow after (min)</label>
              <input type="number" min={1} max={redMins - 1} value={yellowMins} onChange={e => setYellowMins(Math.max(1, parseInt(e.target.value) || 5))}
                className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-red-500 dark:text-red-400 mb-1">Red after (min)</label>
              <input type="number" min={yellowMins + 1} value={redMins} onChange={e => setRedMins(Math.max(yellowMins + 1, parseInt(e.target.value) || 10))}
                className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-400" />
            </div>
          </div>
        )}
      </div>

      {/* Alert thresholds */}
      <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
        <h5 className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Alert banners</h5>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={alertThresholds.allOffline}
              onChange={e => setAlertThresholds({ ...alertThresholds, allOffline: e.target.checked })} className="rounded" />
            Alert when all agents go offline
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Queue depth alert (conversations)</label>
              <input type="number" min={1} value={alertThresholds.queueDepth}
                onChange={e => setAlertThresholds({ ...alertThresholds, queueDepth: Math.max(1, parseInt(e.target.value) || 8) })}
                className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Missed/abandoned alert (today)</label>
              <input type="number" min={1} value={alertThresholds.missedToday}
                onChange={e => setAlertThresholds({ ...alertThresholds, missedToday: Math.max(1, parseInt(e.target.value) || 5) })}
                className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500">Longest wait alert uses the red threshold above ({redMins} min).</p>
        </div>
      </div>

      {/* Dismissed alerts */}
      {dismissedIds.length > 0 && (
        <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Snoozed / hidden alerts</h5>
            <button onClick={onResetAll} className="text-xs text-blue-500 hover:underline">Reset all</button>
          </div>
          <div className="space-y-1.5">
            {dismissedIds.map(id => {
              const d = dismissed[id];
              const label = ALERT_LABELS[id] ?? id;
              const desc = d?.permanent ? "Hidden permanently" : `Snoozed until ${new Date(d.until!).toLocaleTimeString()}`;
              return (
                <div key={id} className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>{label} — <span className="italic">{desc}</span></span>
                  <button onClick={() => onResetOne(id)} className="text-blue-500 hover:underline ml-3 shrink-0">Show</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Superadmin site picker ────────────────────────────────────────────────────

interface SiteOption { SiteId: number; CompanyName: string; }

function SuperAdminPicker({ sites, selectedId, selectedName, onSelect, onClear }: {
  sites: SiteOption[]; selectedId?: number; selectedName?: string;
  onSelect: (id: number, name: string) => void; onClear: () => void;
}) {
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return sites.slice(0, 20);
    return sites.filter(s => s.CompanyName?.toLowerCase().includes(q) || String(s.SiteId).includes(q)).slice(0, 20);
  }, [sites, query]);

  return (
    <div className="flex items-center gap-3 flex-wrap py-2 px-4 mb-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700">
      <span className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide shrink-0">Superadmin</span>
      <div ref={ref} className="relative flex-1 min-w-48 max-w-72">
        <input
          type="text"
          placeholder="View any site by name or ID…"
          value={selectedId ? `${selectedName} (#${selectedId})` : query}
          onFocus={() => { setQuery(""); setOpen(true); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          className="w-full px-3 py-1.5 text-sm border border-violet-300 dark:border-violet-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400"
        />
        {open && filtered.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
            {filtered.map(s => (
              <button key={s.SiteId}
                className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 dark:hover:bg-violet-900/30 text-slate-700 dark:text-slate-300 flex items-center justify-between"
                onClick={() => { onSelect(s.SiteId, s.CompanyName); setQuery(""); setOpen(false); }}>
                <span className="truncate">{s.CompanyName}</span>
                <span className="text-xs text-slate-400 ml-2 shrink-0">#{s.SiteId}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedId && (
        <button onClick={onClear} className="text-xs px-2 py-1 rounded border border-violet-300 dark:border-violet-600 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/40 shrink-0">
          ← Back to Velaro
        </button>
      )}
    </div>
  );
}

// Prominent banner shown when actually viewing a different site
function ViewingBanner({ siteName, siteId, onClear }: { siteName: string; siteId: number; onClear: () => void }) {
  return (
    <div className="rounded-lg px-4 py-3 mb-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-400 dark:border-orange-600 flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <svg className="w-4 h-4 text-orange-600 dark:text-orange-400 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
        </svg>
        <span className="font-bold text-sm text-orange-700 dark:text-orange-300">Velaro Admin View</span>
        <span className="text-orange-600 dark:text-orange-400 text-sm">—</span>
        <span className="text-orange-700 dark:text-orange-300 text-sm font-medium truncate">
          {siteName} <span className="font-normal text-orange-500 dark:text-orange-400">(Site #{siteId})</span>
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-orange-500 dark:text-orange-400">Read-only view</span>
        <button
          onClick={onClear}
          className="text-sm px-3 py-1 rounded-lg bg-orange-600 dark:bg-orange-700 text-white hover:bg-orange-700 dark:hover:bg-orange-600 font-medium"
        >
          ← Back to Velaro
        </button>
      </div>
    </div>
  );
}

// ── Prefs / localStorage ──────────────────────────────────────────────────────

interface DashboardPrefs {
  showWarnings: boolean;
  yellowMins: number;
  redMins: number;
  alertThresholds: AlertThresholds;
  dismissedAlerts: DismissedAlerts;
}

const PREFS_KEY = "dashboard_queue_prefs";
const DEFAULT_THRESHOLDS: AlertThresholds = { queueDepth: 8, missedToday: 5, allOffline: true };

function loadPrefs(): DashboardPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      // Migrate from old dismissedWarnings.coverageGap shape
      const dismissedAlerts: DismissedAlerts = p.dismissedAlerts ?? {};
      if (p.dismissedWarnings?.coverageGap) dismissedAlerts["coverage_gap"] = p.dismissedWarnings.coverageGap;
      return {
        showWarnings: p.showWarnings ?? true,
        yellowMins: p.yellowMins ?? 5,
        redMins: p.redMins ?? 10,
        alertThresholds: p.alertThresholds ?? DEFAULT_THRESHOLDS,
        dismissedAlerts
      };
    }
  } catch {}
  return { showWarnings: true, yellowMins: 5, redMins: 10, alertThresholds: DEFAULT_THRESHOLDS, dismissedAlerts: {} };
}

const COVERAGE_ORDER: Record<string, number> = { no_agents: 0, no_coverage: 1, covered: 2 };

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const api = useApi();

  // Superadmin: probe viewer-sites on mount — if 200, show picker
  const [adminSites, setAdminSites] = React.useState<SiteOption[] | null>(null);
  const [overrideSiteId, setOverrideSiteId] = React.useState<number | undefined>(undefined);
  const [overrideSiteName, setOverrideSiteName] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    api.admin.get("LiveDashboard/viewer-sites").then(async r => {
      if (r.ok) setAdminSites((await r.json()) as SiteOption[]);
    }).catch(() => {/* non-superadmin */});
  }, [api]);

  const { data, streamStatus, lastUpdated } = useLiveDashboard(overrideSiteId);

  const prefs = loadPrefs();
  const [showWarnings, setShowWarnings]           = React.useState(prefs.showWarnings);
  const [yellowMins, setYellowMins]               = React.useState(prefs.yellowMins);
  const [redMins, setRedMins]                     = React.useState(prefs.redMins);
  const [alertThresholds, setAlertThresholds]     = React.useState(prefs.alertThresholds);
  const [dismissedAlerts, setDismissedAlerts]     = React.useState<DismissedAlerts>(prefs.dismissedAlerts);
  const [showSettings, setShowSettings]           = React.useState(false);
  const [teamSearch, setTeamSearch]               = React.useState("");
  const [expandOverride, setExpandOverride]       = React.useState<boolean | null>(null);

  React.useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ showWarnings, yellowMins, redMins, alertThresholds, dismissedAlerts }));
    } catch {}
  }, [showWarnings, yellowMins, redMins, alertThresholds, dismissedAlerts]);

  const alerts = React.useMemo(
    () => data ? computeAlerts(data, alertThresholds, redMins) : [],
    [data, alertThresholds, redMins]
  );

  function handleDismiss(id: string, type: "1h" | "4h" | "permanent") {
    setDismissedAlerts(prev => ({
      ...prev,
      [id]: type === "permanent" ? { permanent: true } : { until: Date.now() + (type === "1h" ? 3_600_000 : 14_400_000) }
    }));
  }

  const longestWait = data?.kpis?.longestWaitMinutes ?? 0;
  const waitWarn = showWarnings && longestWait > yellowMins;

  return (
    <div className="p-3 sm:p-6 bg-slate-50 dark:bg-slate-900 min-h-screen">

      {/* Superadmin quick-link to Live Monitor */}
      {adminSites && adminSites.length > 0 && (
        <div className="flex justify-end mb-3">
          <Link
            to="/admin/live-monitor"
            className="text-xs text-slate-500 hover:text-blue-600 bg-white rounded-lg px-3 py-1.5 shadow-sm border border-slate-200 hover:border-blue-300 transition-colors"
          >
            Live Monitor →
          </Link>
        </div>
      )}

      {/* Superadmin site picker — only for Velaro internal users */}
      {adminSites && adminSites.length > 0 && (
        <SuperAdminPicker
          sites={adminSites}
          selectedId={overrideSiteId}
          selectedName={overrideSiteName}
          onSelect={(id, name) => { setOverrideSiteId(id); setOverrideSiteName(name); }}
          onClear={() => { setOverrideSiteId(undefined); setOverrideSiteName(undefined); }}
        />
      )}

      {/* Prominent viewing banner — shown only when a site override is active */}
      {overrideSiteId && overrideSiteName && (
        <ViewingBanner
          siteName={overrideSiteName}
          siteId={overrideSiteId}
          onClear={() => { setOverrideSiteId(undefined); setOverrideSiteName(undefined); }}
        />
      )}

      {/* Connection bar */}
      <ConnectionBar status={streamStatus} lastUpdated={lastUpdated} isManagerView={data?.isManagerView ?? false} />

      {/* Settings panel */}
      {showSettings && (
        <ThresholdSettings
          showWarnings={showWarnings} setShowWarnings={setShowWarnings}
          yellowMins={yellowMins} setYellowMins={setYellowMins}
          redMins={redMins} setRedMins={setRedMins}
          alertThresholds={alertThresholds} setAlertThresholds={setAlertThresholds}
          dismissed={dismissedAlerts}
          onResetAll={() => setDismissedAlerts({})}
          onResetOne={id => setDismissedAlerts(prev => { const n = { ...prev }; delete n[id]; return n; })}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* KPI strip — row 1 */}
      {data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
            <KpiCard label="Online" value={data.kpis.agentsOnline} color="green" tip="Agents with a status other than Offline right now." />
            <KpiCard label="Available" value={data.kpis.agentsAvailable} color="green" tip="Agents whose status is Available — they can accept new chats." />
            <KpiCard label="Busy" value={data.kpis.agentsBusy} tip="Agents who are logged in but not Available (at capacity, custom status, etc.)." />
            <KpiCard label="Open Chats" value={data.kpis.openConversations} tip="All open conversations — includes bot-active, async (email/ticket), and human-assigned." />
            <KpiCard label="In Queue" value={data.kpis.queuedConversations} warn={data.kpis.queuedConversations > 0} sub="waiting for agent"
              tip="Conversations actively waiting for a human agent on real-time channels. Email and ticket are excluded." />
            <KpiCard label="Bot Active" value={data.kpis.botActive} color="blue" sub="handled by bot"
              tip="Open conversations handled by a bot with no human agent assigned — NOT in the queue." />
          </div>
          {/* KPI strip — row 2 */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            <KpiCard label="Resolved Today" value={data.kpis.resolvedToday} color="green"
              tip="Conversations resolved since midnight UTC today." />
            <KpiCard label="Missed / Abandoned" value={data.kpis.missedAbandonedToday}
              color={data.kpis.missedAbandonedToday > 0 ? "red" : undefined} sub="today"
              tip="Conversations that ended without an agent connecting. Resets at midnight UTC." />
            <KpiCard label="Longest Wait" value={formatMinutes(longestWait)} warn={waitWarn} sub="queue only"
              tip="Longest current wait in the human queue. Excludes bots, email/ticket, and conversations open over 60 minutes." />
            <KpiCard label="Avg Wait" value={formatMinutes(data.kpis.avgWaitMinutes)} sub="queue only"
              tip="Average wait across first-time queue entries on real-time channels." />
            <KpiCard label="Avg Handle Time" value={formatMinutes(data.kpis.avgHandleTimeMinutes)} sub="resolved today"
              tip="Average time from conversation start to resolution, for conversations resolved today." />
            <KpiCard label="Avg First Response" value={formatMinutes(data.kpis.avgFirstResponseMinutes)} sub="resolved today"
              tip="Average time from conversation start to the first agent message, for conversations resolved today." />
          </div>
        </>
      ) : (
        <>
          {[0, 1].map(row => (
            <div key={row} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-lg p-4 flex flex-col bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 animate-pulse">
                  <div className="h-2.5 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                  <div className="h-7 w-10 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
              ))}
            </div>
          ))}
          <div className="text-center text-sm text-slate-400 dark:text-slate-500 mb-4 flex items-center justify-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-slate-400 animate-pulse" />
            {streamStatus === "connecting" ? "Connecting to live data..." : "Reconnecting..."}
          </div>
        </>
      )}

      {/* Alert strip — computed from live data */}
      <AlertStrip alerts={alerts} dismissed={dismissedAlerts} onDismiss={handleDismiss} />

      {/* Secondary widgets */}
      {data && (data.topAgents?.length > 0 || data.channelBreakdown?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {data.topAgents?.length > 0 && <TopAgentsWidget agents={data.topAgents} />}
          {data.channelBreakdown?.length > 0 && <ChannelBreakdownWidget channels={data.channelBreakdown} />}
        </div>
      )}

      {/* Methodology note */}
      {data && (
        <div className="mt-4 rounded-lg px-4 py-3 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-medium text-slate-600 dark:text-slate-300">How these numbers are calculated&nbsp;&mdash;</span>{" "}
          Wait times cover only real-time channels waiting for a human agent for the first time.
          Email, tickets, bot-active, re-queued, and conversations open over 60 minutes are excluded.
          Wait time uses <em>QueuedAt</em> (when the conversation entered the human queue), so bot handling time before handoff does not inflate the figure.
          "Resolved Today" and timing metrics reset at midnight UTC.{" "}
          <strong>Historical reports</strong> include all channels and use StartTimestamp, so live values are often lower — both are correct, they answer different questions.
          Hover any metric label for a full explanation.
        </div>
      )}

      {/* Team cards header */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap mt-6">
        <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
          Teams{data && data.teams.length > 6 && ` (${data.teams.length})`}
        </h2>
        <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
          {data && data.teams.length > 0 && (
            <button
              onClick={() => setExpandOverride(v => v === false ? true : false)}
              className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                {expandOverride === false
                  ? <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  : <path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clipRule="evenodd" />
                }
              </svg>
              {expandOverride === false ? "Expand all" : "Collapse all"}
            </button>
          )}
          {data && data.teams.length > 6 && (
            <input type="search" value={teamSearch} onChange={e => setTeamSearch(e.target.value)}
              placeholder="Filter teams…" aria-label="Filter teams by name"
              className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 w-48" />
          )}
          <button onClick={() => setShowSettings(v => !v)}
            className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1"
            title="Configure alert thresholds">
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            Alert settings
          </button>
        </div>
      </div>

      {/* Team cards */}
      {data ? (
        data.teams.length === 0 ? (
          <div className="rounded-lg p-6 sm:p-12 text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500">
            No teams found.
          </div>
        ) : (() => {
          const filtered = data.teams
            .filter(t => t != null && (!teamSearch || t.teamName.toLowerCase().includes(teamSearch.toLowerCase())))
            .sort((a, b) => (COVERAGE_ORDER[a.coverageStatus ?? "covered"] ?? 2) - (COVERAGE_ORDER[b.coverageStatus ?? "covered"] ?? 2));
          return filtered.length === 0 ? (
            <div className="rounded-lg p-8 text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 text-sm">
              No teams match "{teamSearch}"
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map(team => (
                <SectionErrorBoundary key={team.teamId} label={team.teamName}>
                  <TeamCard team={team} warnYellowMins={yellowMins} warnRedMins={redMins}
                    showWarnings={showWarnings} expandOverride={expandOverride} />
                </SectionErrorBoundary>
              ))}
            </div>
          );
        })()
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-lg p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 animate-pulse flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-3 w-36 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
                <div className="space-y-1 text-right">
                  <div className="h-6 w-6 bg-slate-200 dark:bg-slate-700 rounded ml-auto" />
                  <div className="h-2.5 w-10 bg-slate-200 dark:bg-slate-700 rounded ml-auto" />
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-3 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-3 w-14 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-full bg-slate-100 dark:bg-slate-700/50 rounded" />
                <div className="h-3 w-full bg-slate-100 dark:bg-slate-700/50 rounded" />
                <div className="h-3 w-3/4 bg-slate-100 dark:bg-slate-700/50 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
